import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Reusable search hook with debounce
 * Manages search query and debounced reset to page 1
 * 
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 500)
 * @param {Function} onSearchChange - Optional callback when search changes (after debounce)
 * @returns {Object} Search state and handlers
 */
function useSearch(debounceMs = 500, onSearchChange = null) {
  const [searchQuery, setSearchQuery] = useState(''); // Immediate value for input field
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(''); // Debounced value for API calls
  const searchTimeoutRef = useRef(null);
  const onSearchChangeRef = useRef(onSearchChange);
  const prevSearchQueryRef = useRef(searchQuery);

  // Store the latest callback in a ref to avoid re-running effect when callback changes
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  // Debounced search - updates debouncedSearchQuery after delay and calls callback
  // CRITICAL: Only depend on searchQuery, not onSearchChange
  // This prevents the effect from running when the callback changes (which happens on every render
  // because components pass inline arrow functions)
  useEffect(() => {
    // Only call callback if search query actually changed (not just callback changed)
    const searchQueryChanged = prevSearchQueryRef.current !== searchQuery;
    
    if (!searchQueryChanged) {
      // Search query hasn't changed, don't do anything
      return;
    }
    
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set up debounced callback
    searchTimeoutRef.current = setTimeout(() => {
      // Update debounced search query (this will trigger API calls in components)
      setDebouncedSearchQuery(searchQuery);
      
      // Use ref to get latest callback without triggering re-run
      if (onSearchChangeRef.current) {
        onSearchChangeRef.current(searchQuery);
      }
      // Update previous value after callback is called
      prevSearchQueryRef.current = searchQuery;
    }, debounceMs);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, debounceMs]); // Removed onSearchChange from dependencies

  const handleSearchChange = useCallback((e) => {
    const value = e.currentTarget?.value || e.target?.value || '';
    setSearchQuery(value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    // Immediately clear debounced query too
    setDebouncedSearchQuery('');
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (onSearchChangeRef.current) {
      onSearchChangeRef.current('');
    }
  }, []);

  return {
    searchQuery, // Immediate value for input field
    debouncedSearchQuery, // Debounced value for API calls - use this in useEffect dependencies
    setSearchQuery,
    handleSearchChange,
    clearSearch
  };
}

export default useSearch;

